import React, { useEffect, useState } from "react";
import { Loader2, Search, Boxes, SlidersHorizontal, History, AlertTriangle } from "lucide-react";
import api, { formatApiError, money } from "../lib/api";
import { smartSearch } from "../lib/search";
import { useSortable } from "../lib/sortable";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumberInput } from "../components/NumberInput";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

const MOV_COLORS = { entrada: "bg-green-100 text-green-700", salida: "bg-red-100 text-red-700", ajuste: "bg-amber-100 text-amber-700", reserva: "bg-blue-100 text-blue-700", liberacion: "bg-slate-200 text-slate-700" };

export default function Inventory() {
  const { can } = useAuth();
  const { Th, sortRows } = useSortable("", "asc", { avail: (p) => Number(p.stock || 0) - Number(p.reserved || 0) });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [alerts, setAlerts] = useState(0);
  const [adjust, setAdjust] = useState(null);
  const [adjForm, setAdjForm] = useState({ mode: "in", qty: 0, reason: "" });
  const [kardex, setKardex] = useState(null);
  const [movs, setMovs] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/products"), api.get("/inventory/alerts")])
      .then(([p, a]) => { setProducts(p.data); setAlerts(a.data.count); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdjust = (p) => { setAdjust(p); setAdjForm({ mode: "in", qty: 0, reason: "" }); };
  const saveAdjust = async () => {
    try { await api.post("/inventory/adjust", { product_id: adjust.id, ...adjForm }); toast.success("Existencia ajustada"); setAdjust(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openKardex = async (p) => { setKardex(p); const r = await api.get(`/inventory/movements?product_id=${p.id}`); setMovs(r.data); };

  const filtered = smartSearch(products, q, ["code", "description", "brand", "category"]);

  return (
    <div className="space-y-5">
      {alerts > 0 && (
        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-600 rounded-sm px-4 py-2.5 text-sm" data-testid="low-stock-alert">
          <AlertTriangle className="h-4 w-4" /> <strong>{alerts}</strong> producto(s) con existencia igual o por debajo del mínimo.
        </div>
      )}
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto..." className="pl-9 rounded-sm" />
      </div>

      <Card className="rounded-sm border shadow-none overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          filtered.length === 0 ? <div className="flex flex-col items-center py-16 text-muted-foreground"><Boxes className="h-8 w-8 mb-2" /><p className="text-sm">Sin productos.</p></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inventory-table">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <Th field="code">Código</Th><Th field="description">Descripción</Th>
                  <Th field="stock" align="right" className="text-right">Físico</Th><Th field="reserved" align="right" className="text-right">Reservado</Th>
                  <Th field="avail" align="right" className="text-right">Disponible</Th><Th field="cost" align="right" className="text-right">Costo</Th>
                  <th className="px-4 py-3 font-bold text-right">Acciones</th></tr></thead>
                <tbody>
                  {sortRows(filtered).map((p) => {
                    const stock = Number(p.stock || 0), reserved = Number(p.reserved || 0), avail = stock - reserved;
                    const low = Number(p.min_stock || 0) > 0 && stock <= Number(p.min_stock || 0);
                    return (
                      <tr key={p.id} className="border-t border-border hover:bg-muted/50" data-testid={`inv-row-${p.code}`}>
                        <td className="px-4 py-2.5 font-mono text-xs">{p.code}</td>
                        <td className="px-4 py-2.5 font-medium">{p.description}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{low ? <Badge className="bg-amber-100 text-amber-700 rounded-sm font-mono">{stock}</Badge> : stock}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{reserved}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{avail}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs">{money(p.currency, p.cost)}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Kardex" onClick={() => openKardex(p)} data-testid={`kardex-${p.code}`}><History className="h-4 w-4" /></Button>
                          {can("inventory:adjust") && <Button variant="ghost" size="icon" className="h-8 w-8" title="Ajustar" onClick={() => openAdjust(p)} data-testid={`adjust-${p.code}`}><SlidersHorizontal className="h-4 w-4" /></Button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="adjust-dialog">
          {adjust && (<>
            <DialogHeader><DialogTitle className="font-heading">Ajustar existencia</DialogTitle>
              <DialogDescription>{adjust.code} · {adjust.description} · Actual: {Number(adjust.stock || 0)}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Tipo</Label>
                <Select value={adjForm.mode} onValueChange={(v) => setAdjForm({ ...adjForm, mode: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="adjust-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in">Entrada (+)</SelectItem><SelectItem value="out">Salida (−)</SelectItem><SelectItem value="set">Fijar cantidad exacta</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Cantidad</Label>
                <NumberInput className="rounded-sm" value={adjForm.qty} onValueChange={(v) => setAdjForm({ ...adjForm, qty: v })} data-testid="adjust-qty" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Motivo</Label>
                <Textarea className="rounded-sm" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} data-testid="adjust-reason" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setAdjust(null)}>Cancelar</Button>
              <Button className="rounded-sm font-bold" onClick={saveAdjust} data-testid="save-adjust-btn">Aplicar</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!kardex} onOpenChange={(o) => !o && setKardex(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kardex-dialog">
          {kardex && (<>
            <DialogHeader><DialogTitle className="font-heading">Kardex · {kardex.code}</DialogTitle>
              <DialogDescription>{kardex.description}</DialogDescription></DialogHeader>
            <table className="w-full text-sm border border-border rounded-sm">
              <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground"><th className="px-2 py-2">Fecha</th><th className="px-2 py-2">Tipo</th><th className="px-2 py-2 text-right">Cant.</th><th className="px-2 py-2">Ref.</th><th className="px-2 py-2 text-right">Saldo</th></tr></thead>
              <tbody>
                {movs.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">Sin movimientos.</td></tr>}
                {movs.map((m) => (
                  <tr key={m.id} className="border-t border-border"><td className="px-2 py-1.5 text-xs">{new Date(m.created_at).toLocaleString("es-GT")}</td>
                    <td className="px-2 py-1.5"><Badge className={`rounded-sm text-[10px] ${MOV_COLORS[m.type] || ""}`}>{m.type}</Badge></td>
                    <td className="px-2 py-1.5 text-right font-mono">{m.direction}{m.qty}</td>
                    <td className="px-2 py-1.5 text-xs font-mono">{m.ref_number || m.reason}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{m.stock_after}</td></tr>
                ))}
              </tbody>
            </table>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
